/**
 * Playtest control panel (O-026) — every developer affordance the techdemo
 * offers, reachable by clicking instead of typing.
 *
 * Replaces the raw-DOM `?devmenu=1` F9 overlay (AD-217 item 5). Registering in
 * the PANELS table means the window manager supplies dragging, Esc-close, the
 * launcher button, the hotkey and capture-mode suppression for free, and the
 * `--pxl-*` theme applies — none of which the hand-built overlay had.
 *
 * Dev builds only: `panels/index.tsx` registers this behind `devToolsEnabled()`,
 * a Vite compile-time literal, so the production bundle drops it entirely.
 *
 * Client-local and presentation-only. Teleports re-login as characters that
 * already exist in the `techdemo` seed (the AD-216 capture-set pattern) — no new
 * wire message, no granted power.
 *
 * Design: docs/features/playtest-panel.md
 */

import { useCallback, useEffect, useState } from 'react';

import { CAMERA_PRESETS, CAPTURE_KEYS } from '../../game/capture.js';
import { devBridge } from '../../game/devBridge.js';
import {
  DEV_FLAGS,
  clearDevOverrides,
  devFlagIsOn,
  rawSearch,
  readDevOverrides,
  resolveDevFlagState,
  writeDevOverride,
} from '../../game/devFlags.js';
import type { DevFlagDef, DevFlagOverrides } from '../../game/devFlags.js';
import pois from '../../game/dungeon/playtest-pois.json' with { type: 'json' };
import { useGame } from '../net-context.js';
import { Empty, Field, SectionTitle, Stat, Tabs } from '../widgets.js';
import { routedDevZones } from '../../net/devZones.js';
import { applyZoneRoute, poiReach, ZONE_ROUTE_KEY } from './playtestJump.js';

interface Poi {
  id: string;
  label: string;
  category: string;
  email: string;
  password: string;
  character: string;
  /** The seeded character's checkpoint zone — a jump lands there only if the server hosts it. */
  zone: string;
  hint: string;
}

const POIS: Poi[] = (pois as { pois: Poi[] }).pois;

/** Zones this dev server routes to their own backend (`npm run playtest:zones`). */
const ROUTED_ZONES = routedDevZones();

function sessionStore(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

/** The four difficulties the entrance catalog defines (mirrors layer.ts's quota table). */
const DIFFICULTIES = ['story', 'adventurer', 'veteran', 'mythic'] as const;

const TABS = [
  { id: 'world', label: 'World' },
  { id: 'dungeons', label: 'Dungeons' },
  { id: 'render', label: 'Render' },
  { id: 'capture', label: 'Capture' },
];

export function PlaytestPanel(): JSX.Element {
  const [tab, setTab] = useState('world');
  const [note, setNote] = useState('Click to jump, generate or toggle — nothing needs typing.');

  return (
    <div className="pxl-list">
      <Tabs tabs={TABS} active={tab} onSelect={setTab} />
      <div className="pxl-muted" style={{ fontSize: 11, margin: '4px 0 2px' }}>
        {note}
      </div>
      {tab === 'world' ? <WorldTab onNote={setNote} /> : null}
      {tab === 'dungeons' ? <DungeonsTab onNote={setNote} /> : null}
      {tab === 'render' ? <RenderTab onNote={setNote} /> : null}
      {tab === 'capture' ? <CaptureTab onNote={setNote} /> : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// World — POI teleports
// ---------------------------------------------------------------------------

function WorldTab({ onNote }: { onNote: (s: string) => void }): JSX.Element {
  const [busy, setBusy] = useState<string | null>(null);
  // q-o071-playtest-fixes (d): one zone per server process — say which, and offer only
  // the jumps it can honour (playtestJump.ts).
  const hosted = useGame().state.ack?.zoneId ?? null;
  const [route, setRoute] = useState<string | null>(() => {
    try {
      return sessionStore()?.getItem(ZONE_ROUTE_KEY) ?? null;
    } catch {
      return null;
    }
  });

  const jump = useCallback(
    async (poi: Poi) => {
      const hook = window.__GC_DEVJUMP;
      if (!hook) {
        onNote('Jump hook unavailable — is this a dev build with the techdemo seed loaded?');
        return;
      }
      setBusy(poi.id);
      onNote(`Jumping to ${poi.label}…`);
      // q-playtest-cross-zone-jump: a routed zone is served by its own backend — point the
      // client there first, so login, token and socket all reach it.
      if (poiReach(poi, hosted, ROUTED_ZONES).kind === 'route') {
        applyZoneRoute(poi.zone, window.location.origin, sessionStore());
        setRoute(poi.zone);
      }
      try {
        const ok = await hook(poi.email, poi.password, poi.character);
        onNote(
          ok
            ? `Arrived at ${poi.label}.`
            : `Jump failed — is "${poi.character}" in the loaded seed profile?`,
        );
      } catch {
        onNote(`Jump to ${poi.label} threw — see the console.`);
      } finally {
        setBusy(null);
      }
    },
    [onNote, hosted],
  );

  const categories = new Map<string, Poi[]>();
  for (const poi of POIS) {
    const list = categories.get(poi.category) ?? [];
    list.push(poi);
    categories.set(poi.category, list);
  }

  if (POIS.length === 0) return <Empty>No points of interest are defined.</Empty>;

  return (
    <>
      <Stat k="Server zone" v={hosted ?? 'not joined'} />
      {ROUTED_ZONES.length > 0 ? (
        <>
          <Stat k="Routed zones" v={ROUTED_ZONES.join(', ')} />
          {route !== null ? (
            <button
              className="pxl-btn"
              onClick={() => {
                applyZoneRoute(null, window.location.origin, sessionStore());
                setRoute(null);
                window.location.reload();
              }}
              style={{ margin: '2px 0 4px' }}
            >
              Back to default server
            </button>
          ) : null}
        </>
      ) : (
        <div className="pxl-muted" style={{ fontSize: 11, margin: '2px 0 4px' }}>
          The server hosts one zone per process. Start with <code>npm run playtest:zones</code> to
          jump between zones, or restart the backend with the GC_START_ZONE shown (PLAYTEST.md,
          &quot;Which zone boots&quot;).
        </div>
      )}
      {[...categories].map(([category, list]) => (
        <div key={category}>
          <SectionTitle>{category}</SectionTitle>
          {list.map((poi) => {
            const reach = poiReach(poi, hosted, ROUTED_ZONES);
            return (
              <div
                key={poi.id}
                style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '3px 0' }}
              >
                <button
                  className="pxl-btn"
                  disabled={busy !== null || reach.kind === 'restart'}
                  onClick={() => void jump(poi)}
                  style={{ flexShrink: 0 }}
                >
                  {busy === poi.id ? '…' : 'Jump'}
                </button>
                <div style={{ lineHeight: 1.25 }}>
                  {poi.label}
                  <br />
                  <span className="pxl-muted" style={{ fontSize: 11 }}>
                    {reach.kind === 'restart' ? `Needs ${reach.command} · ` : null}
                    {poi.hint}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      ))}
    </>
  );
}

// ---------------------------------------------------------------------------
// Dungeons — enable, generate a preview, inspect the placed shrines
// ---------------------------------------------------------------------------

/** The shrine summary shape `DungeonWorldLayer.listSites()` returns. */
interface ShrineSite {
  id: string;
  difficultyId: string;
  x: number;
  z: number;
  phase: string;
}

function DungeonsTab({ onNote }: { onNote: (s: string) => void }): JSX.Element {
  const [overrides, setOverrides] = useState<DevFlagOverrides>(() => readDevOverrides());
  const [sites, setSites] = useState<ShrineSite[]>([]);
  const [busy, setBusy] = useState(false);

  const refreshSites = useCallback(() => {
    setSites(devBridge()?.dungeonLayer()?.listSites() ?? []);
  }, []);

  useEffect(() => {
    refreshSites();
    const timer = setInterval(refreshSites, 1000);
    return () => clearInterval(timer);
  }, [refreshSites]);

  const preview = useCallback(
    async (difficulty: string) => {
      const layer = devBridge()?.dungeonLayer();
      if (!layer) {
        onNote('Enable "Dungeon shrines" below, then reload — the layer is not built yet.');
        return;
      }
      setBusy(true);
      onNote(`Generating a ${difficulty} dungeon…`);
      try {
        const entered = await layer.previewDifficulty(difficulty);
        onNote(entered ? `Inside a ${difficulty} dungeon — Esc to leave.` : 'Generation failed.');
      } catch {
        onNote('Generation threw — see the console.');
      } finally {
        setBusy(false);
        refreshSites();
      }
    },
    [onNote, refreshSites],
  );

  const layerLive = devBridge()?.dungeonLayer() != null;

  return (
    <>
      <SectionTitle>Layer</SectionTitle>
      {DEV_FLAGS.filter((f) => f.group === 'dungeons').map((def) => (
        <FlagRow
          key={def.id}
          def={def}
          overrides={overrides}
          onChange={(value) => {
            writeDevOverride(def.id, value);
            setOverrides(readDevOverrides());
            onNote('Saved — reload to apply.');
          }}
        />
      ))}
      <Stat k="Layer built" v={layerLive ? 'yes' : 'no (reload with shrines on)'} />

      <SectionTitle>Generate a preview</SectionTitle>
      <div className="pxl-muted" style={{ fontSize: 11, marginBottom: 4 }}>
        Builds an interior client-side and drops you inside it, without walking to a shrine.
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {DIFFICULTIES.map((difficulty) => (
          <button
            key={difficulty}
            className="pxl-btn"
            disabled={busy || !layerLive}
            onClick={() => void preview(difficulty)}
            style={{ flex: 1, minWidth: 68 }}
          >
            {difficulty}
          </button>
        ))}
      </div>

      <SectionTitle>Shrines in this zone</SectionTitle>
      {sites.length === 0 ? (
        <Empty>None placed — enable the layer and reload.</Empty>
      ) : (
        sites.map((site) => (
          <Stat
            key={site.id}
            k={site.difficultyId}
            v={`(${Math.round(site.x)}, ${Math.round(site.z)}) · ${site.phase}`}
          />
        ))
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Render — the flags that used to be URL-only
// ---------------------------------------------------------------------------

function RenderTab({ onNote }: { onNote: (s: string) => void }): JSX.Element {
  const [overrides, setOverrides] = useState<DevFlagOverrides>(() => readDevOverrides());

  const set = useCallback(
    (id: string, value: string) => {
      writeDevOverride(id, value);
      setOverrides(readDevOverrides());
      onNote('Saved — reload to apply.');
    },
    [onNote],
  );

  return (
    <>
      <SectionTitle>Rendering</SectionTitle>
      {DEV_FLAGS.filter((f) => f.group === 'render').map((def) => (
        <FlagRow
          key={def.id}
          def={def}
          overrides={overrides}
          onChange={(value) => set(def.id, value)}
        />
      ))}
      <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
        <button className="pxl-btn" onClick={() => window.location.reload()} style={{ flex: 1 }}>
          Apply &amp; reload
        </button>
        <button
          className="pxl-btn"
          onClick={() => {
            clearDevOverrides();
            setOverrides(readDevOverrides());
            onNote('All overrides cleared — reload to apply.');
          }}
          style={{ flex: 1 }}
        >
          Reset all
        </button>
      </div>
    </>
  );
}

/** One flag row: a checkbox, a select or a text field, plus its provenance. */
function FlagRow({
  def,
  overrides,
  onChange,
}: {
  def: DevFlagDef;
  overrides: DevFlagOverrides;
  onChange: (value: string) => void;
}): JSX.Element {
  const state = resolveDevFlagState(def, rawSearch(), overrides);
  // A flag pinned in the URL cannot be changed from here — the URL always wins,
  // so an editable control would silently do nothing. Say so instead.
  const pinned = state.source === 'url';

  let control: JSX.Element;
  if (def.kind === 'boolean') {
    control = (
      <input
        type="checkbox"
        disabled={pinned}
        checked={devFlagIsOn(def, state)}
        onChange={(e) => onChange(e.target.checked ? (def.onValue ?? '1') : '')}
      />
    );
  } else if (def.kind === 'enum') {
    control = (
      <select
        className="pxl-select"
        disabled={pinned}
        value={state.value}
        onChange={(e) => onChange(e.target.value)}
      >
        {(def.options ?? []).map((option) => (
          <option key={option.value || 'auto'} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    );
  } else {
    control = (
      <Field value={state.value} placeholder={def.placeholder} onChange={onChange} width={130} />
    );
  }

  return (
    <div style={{ margin: '6px 0' }}>
      <label className="pxl-stat">
        <span className="k">{def.label}</span>
        <span className="v">{control}</span>
      </label>
      <div className="pxl-muted" style={{ fontSize: 11, marginTop: 1 }}>
        {def.help}
        {pinned ? ' · pinned in the URL — remove it from the address bar to edit here.' : null}
        {!pinned && def.applies === 'reload' ? ' · needs a reload.' : null}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Capture — the same pxl.* keys the F4/F3/[ ] hotkeys drive
// ---------------------------------------------------------------------------

function readLocal(key: string, fallback = false): boolean {
  try {
    const raw = localStorage.getItem(key);
    return raw === null ? fallback : raw === '1';
  } catch {
    return fallback;
  }
}

function CaptureTab({ onNote }: { onNote: (s: string) => void }): JSX.Element {
  const [captureMode, setCaptureMode] = useState(() => readLocal(CAPTURE_KEYS.captureMode));
  const [hudHidden, setHudHidden] = useState(() => readLocal(CAPTURE_KEYS.hudHidden));
  const [hideHealthBars, setHideHealthBars] = useState(() =>
    readLocal(CAPTURE_KEYS.captureHideHealthBars, true),
  );
  const [slowOrbit, setSlowOrbit] = useState(() => readLocal(CAPTURE_KEYS.slowOrbit));
  const [preset, setPreset] = useState<string>(() => {
    try {
      return localStorage.getItem(CAPTURE_KEYS.capturePreset) ?? CAMERA_PRESETS[0].id;
    } catch {
      return CAMERA_PRESETS[0].id;
    }
  });

  const write = useCallback((key: string, value: string) => {
    try {
      localStorage.setItem(key, value);
    } catch {
      /* private mode — the hotkeys remain the fallback */
    }
  }, []);

  return (
    <>
      <SectionTitle>Capture mode</SectionTitle>
      <div className="pxl-muted" style={{ fontSize: 11, marginBottom: 4 }}>
        The same preferences the F4 / F3 / [ ] hotkeys drive — one source of truth, so the panel and
        the keys never disagree. Capture mode closes this panel with the rest of the UI.
      </div>
      <label className="pxl-stat">
        <span className="k">Capture mode (hide all UI) — F4</span>
        <input
          type="checkbox"
          checked={captureMode}
          onChange={(e) => {
            setCaptureMode(e.target.checked);
            write(CAPTURE_KEYS.captureMode, e.target.checked ? '1' : '0');
            onNote('Capture mode takes effect on the next F4 toggle or reload.');
          }}
        />
      </label>
      <label className="pxl-stat">
        <span className="k">Hide HUD only — F3</span>
        <input
          type="checkbox"
          checked={hudHidden}
          onChange={(e) => {
            setHudHidden(e.target.checked);
            write(CAPTURE_KEYS.hudHidden, e.target.checked ? '1' : '0');
          }}
        />
      </label>
      <label className="pxl-stat">
        <span className="k">Hide health bars in capture</span>
        <input
          type="checkbox"
          checked={hideHealthBars}
          onChange={(e) => {
            setHideHealthBars(e.target.checked);
            write(CAPTURE_KEYS.captureHideHealthBars, e.target.checked ? '1' : '0');
          }}
        />
      </label>
      <label className="pxl-stat">
        <span className="k">Slow orbit drift</span>
        <input
          type="checkbox"
          checked={slowOrbit}
          onChange={(e) => {
            setSlowOrbit(e.target.checked);
            write(CAPTURE_KEYS.slowOrbit, e.target.checked ? '1' : '0');
          }}
        />
      </label>
      <SectionTitle>Camera preset ([ / ])</SectionTitle>
      <label className="pxl-stat">
        <span className="k">Preset</span>
        <span className="v">
          <select
            className="pxl-select"
            value={preset}
            onChange={(e) => {
              setPreset(e.target.value);
              write(CAPTURE_KEYS.capturePreset, e.target.value);
              onNote('Preset saved — applies on the next capture toggle or reload.');
            }}
          >
            {CAMERA_PRESETS.map((p) => (
              <option key={p.id} value={p.id}>
                {p.label}
              </option>
            ))}
          </select>
        </span>
      </label>
    </>
  );
}
